import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mic, Square, Send, X } from 'lucide-react';
import RecordRTC from 'recordrtc';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceRecorder = ({ onSend, onClose }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [duration, setDuration] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const recorderRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recorderRef.current) recorderRef.current.destroy();
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        } 
      });
      
      // Try to use the best available format for the browser
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/wav'
      ];
      
      let selectedMimeType = 'audio/webm';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }
      
      recorderRef.current = new RecordRTC(stream, {
        type: 'audio',
        mimeType: selectedMimeType,
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 16000, // Optimal for speech recognition
        timeSlice: 1000,
        bitsPerSecond: 128000
      });

      recorderRef.current.startRecording();
      setIsRecording(true);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Please allow microphone access to record voice notes. Make sure no other app is using the microphone.');
    }
  };

  const stopRecording = () => {
    if (!recorderRef.current) return;

    recorderRef.current.stopRecording(() => {
      const blob = recorderRef.current.getBlob();
      setRecordedBlob(blob);
      setAudioURL(URL.createObjectURL(blob));
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      recorderRef.current.stream.getTracks().forEach((track) => track.stop());
    });
  };

  const handleTranscribe = async () => {
    if (!recordedBlob) return;

    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', recordedBlob, 'voice-note.webm');

      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setTranscription(response.data.text);
    } catch (error) {
      console.error('Error transcribing:', error);
      alert('Failed to transcribe audio');
    } finally {
      setTranscribing(false);
    }
  };

  const handleSend = async () => {
    if (!recordedBlob) return;

    try {
      const formData = new FormData();
      formData.append('file', recordedBlob, 'voice-note.webm');

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onSend(transcription || '[Voice Note]', 'voice', response.data.url);
      onClose();
    } catch (error) {
      console.error('Error uploading voice note:', error);
      alert('Failed to send voice note');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#08080C] rounded-3xl shadow-2xl p-6 max-w-md w-full border border-white/10"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Voice Note
          </h3>
          <button
            data-testid="close-voice-recorder-button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {!recordedBlob && (
            <div className="flex flex-col items-center gap-4 py-8">
              <motion.button
                data-testid={isRecording ? 'stop-recording-button' : 'start-recording-button'}
                onClick={isRecording ? stopRecording : startRecording}
                className={`w-24 h-24 rounded-full flex items-center justify-center shadow-lg ${isRecording ? '' : ''}`}
                style={{ 
                  background: isRecording ? '#FF2A2A' : '#00F0FF',
                  boxShadow: isRecording ? '0 0 40px rgba(255,42,42,0.4)' : '0 0 40px rgba(0,240,255,0.4)'
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                animate={isRecording ? { scale: [1, 1.05, 1] } : {}}
                transition={isRecording ? { repeat: Infinity, duration: 1 } : {}}
              >
                {isRecording ? (
                  <Square className="w-10 h-10 text-white" />
                ) : (
                  <Mic className="w-10 h-10 text-black" />
                )}
              </motion.button>

              <div className="text-center">
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatDuration(duration)}
                </p>
                <p className="text-sm text-gray-500">
                  {isRecording ? 'Recording...' : 'Tap to start recording'}
                </p>
              </div>
            </div>
          )}

          {recordedBlob && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/10">
                <audio src={audioURL} controls className="w-full" style={{ filter: 'invert(1) hue-rotate(180deg)' }} />
              </div>

              {transcription && (
                <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/10">
                  <p className="text-sm font-medium mb-2 text-gray-500">Transcription:</p>
                  <p className="text-sm text-white">{transcription}</p>
                </div>
              )}

              <div className="flex gap-2">
                <motion.button
                  data-testid="transcribe-button"
                  onClick={handleTranscribe}
                  disabled={transcribing}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 py-3 rounded-full font-medium bg-[#0F0F16] text-white border border-white/10 hover:bg-white/5 disabled:opacity-50 transition-all"
                >
                  {transcribing ? 'Transcribing...' : 'Transcribe'}
                </motion.button>
                <motion.button
                  data-testid="send-voice-note-button"
                  onClick={handleSend}
                  whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(0,240,255,0.3)' }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 py-3 rounded-full font-semibold text-black bg-[#00F0FF] shadow-lg shadow-[#00F0FF]/20 flex items-center justify-center gap-2 transition-all"
                >
                  <Send className="w-5 h-5" />
                  Send
                </motion.button>
              </div>

              <button
                data-testid="record-again-button"
                onClick={() => {
                  setRecordedBlob(null);
                  setAudioURL(null);
                  setTranscription('');
                  setDuration(0);
                }}
                className="w-full py-2 rounded-full text-sm font-medium text-gray-500 hover:text-white hover:bg-white/5 transition-all"
              >
                Record Again
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VoiceRecorder;